import { useTranslation } from 'react-i18next';
import { AddCircle, TickCircle, Edit2, Trash } from 'iconsax-react';
import Button from '../../../components/atoms/Button';
import CustomCheckbox from '../../../components/atoms/CustomCheckbox';
import InfoText from '../../../components/atoms/InfoText';
import { PlanBenefitItem } from '../../../types/marketplacePlans';

interface PlanBenefitsSectionProps {
  benefits: PlanBenefitItem[];
  isRecommended?: boolean;
  onOpenAddBenefit: () => void;
  onOpenEditBenefit: (benefit: PlanBenefitItem, index: number) => void;
  onDeleteBenefit: (index: number) => void;
  onToggleRecommended: (checked: boolean) => void;
  hideRecommended?: boolean;
}

export default function PlanBenefitsSection({
  benefits,
  isRecommended = false,
  onOpenAddBenefit,
  onOpenEditBenefit,
  onDeleteBenefit,
  onToggleRecommended,
  hideRecommended = false,
}: PlanBenefitsSectionProps) {
  const { t } = useTranslation();

  return (
    <div
      className="flex flex-col gap-3 mt-2"
      data-testid="plan-benefits-section"
    >
      {/* Sub-section header: Title, Counter & Add Button */}
      <div
        className="flex justify-between items-center w-full min-h-[44px]"
        data-testid="plan-benefits-header"
      >
        <div className="flex flex-col" data-testid="plan-benefits-title-count">
          <InfoText
            text={t('home.plan-information-title')}
            className="text-[16px] !font-semibold !text-light-text-primary dark:!text-dark-text-primary leading-tight"
          />
          <InfoText
            text={t('home.information-counter', {
              current: benefits.length,
              total: 5,
            })}
            className="text-[16px] !font-normal !text-light-text-primary dark:!text-dark-text-primary leading-tight"
          />
        </div>

        <Button
          variant="outline"
          type="button"
          size="sm"
          onClick={onOpenAddBenefit}
          disabled={benefits.length >= 5}
          icon={<AddCircle size={20} variant="Bold" className="text-light-text-primary dark:text-dark-text-primary shrink-0" />}
          className="!h-[38px] !rounded-[8px] !px-4 !py-2 !border-light-input-border dark:!border-dark-input-border !text-light-text-primary dark:!text-dark-text-primary !inline-flex !flex-row items-center justify-center !gap-[10px] text-[14px] !font-medium whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="add-benefit-btn"
        >
          {t('home.type-plan-information-btn')}
        </Button>
      </div>

      {/* Benefits list */}
      <div className="flex flex-col gap-3" data-testid="benefits-list">
        {benefits.map((benefit, idx) => (
          <div
            key={benefit.id}
            className="border border-light-input-border dark:border-dark-input-border rounded-[8px] px-4 py-3 flex items-center justify-between gap-4 bg-light-surface-primary dark:bg-dark-surface-primary"
            data-testid={`benefit-card-${idx}`}
          >
            <div className="flex flex-col gap-2 min-w-0" data-testid={`benefit-texts-${idx}`}>
              <div
                className="flex items-center gap-2.5 text-[14px] !font-semibold text-light-text-primary dark:text-dark-text-primary"
                data-testid={`benefit-en-${idx}`}
              >
                <TickCircle size={20} variant="Linear" className="text-light-success shrink-0" />
                <InfoText
                  text={benefit.textEn}
                  maxLength="FULL"
                  className="!text-light-text-primary dark:!text-dark-text-primary text-[14px] !font-semibold leading-tight truncate"
                />
              </div>
              <div
                className="flex items-center gap-2.5 text-[14px] !font-semibold text-light-text-primary dark:text-dark-text-primary"
                data-testid={`benefit-ar-${idx}`}
              >
                <TickCircle size={20} variant="Linear" className="text-light-success shrink-0" />
                <InfoText
                  text={benefit.textAr}
                  maxLength="FULL"
                  className="!text-light-text-primary dark:!text-dark-text-primary text-[14px] !font-semibold leading-tight truncate"
                />
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0" data-testid={`benefit-actions-${idx}`}>
              <button
                type="button"
                onClick={() => onOpenEditBenefit(benefit, idx)}
                className="w-7 h-7 flex items-center justify-center rounded text-light-information dark:text-dark-information hover:!bg-soft-background dark:hover:!bg-dark-surface-primary transition-colors cursor-pointer"
                aria-label="Edit benefit"
                data-testid={`edit-benefit-btn-${idx}`}
              >
                <Edit2 size={16} variant="Bold" />
              </button>
              <button
                type="button"
                onClick={() => onDeleteBenefit(idx)}
                className="w-7 h-7 flex items-center justify-center rounded text-light-warning dark:text-dark-warning hover:!bg-soft-background dark:hover:!bg-dark-surface-primary transition-colors cursor-pointer"
                aria-label="Delete benefit"
                data-testid={`delete-benefit-btn-${idx}`}
              >
                <Trash size={16} variant="Linear" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Mark as Recommended checkbox */}
      {!hideRecommended && (
        <div className="pt-2" data-testid="mark-as-recommended-container">
          <CustomCheckbox
            checked={isRecommended}
            value={isRecommended}
            onChange={(e) => onToggleRecommended(e.target.checked)}
            data-testid="mark-as-recommended-checkbox"
          >
            <InfoText
              text={t('home.mark-as-recommended')}
              className="text-sm !text-light-text-primary dark:!text-dark-text-primary !font-normal inline-block"
            />
          </CustomCheckbox>
        </div>
      )}
    </div>
  );
}
